import { inject, injectable } from 'inversify'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { MouseEventParams } from '@/plugins/cad/module/core/common/event'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'
import type { IPoint } from '../model/common/interface'
import type { IEntity } from '../model/entity/BaseEntity'
import type { CaptureHelper } from './captureHelper'
import type { OrthogonalHelper } from './OrthogonalHelper'
import type { PointCloudHelper } from './pointCloudHelper'
import type { PolarHelper } from './polarHelper'

/**
 * 辅助吸附总协调器。
 *
 */
@injectable()
export class AuxiliaryMouseHelper {
  @inject(TYPES.CaptureHelper) private readonly captureHelper!: CaptureHelper
  @inject(TYPES.OrthogonalHelper) private readonly orthogonalHelper!: OrthogonalHelper
  @inject(TYPES.PolarHelper) private readonly polarHelper!: PolarHelper
  @inject(TYPES.PointCloudHelper) private readonly pointCloudHelper!: PointCloudHelper

  /**
   * 将辅助吸附协调器接入当前工具。
   *
   * @param tool - 当前激活的工具实例
   */
  public injectTool(tool: BaseTool) {
    this.captureHelper.injectTool(tool)
  }

  /** 与当前工具解绑并清理残留的辅助 UI */
  public ejectTool() {
    this.clearUI()
    this.captureHelper.ejectTool()
  }

  /**
   * 统一处理一次鼠标事件对应的吸附流程。
   *
   * @param params - 鼠标事件参数
   * @param options - 吸附选项，包含正交/极轴参考起点与候选实体
   * @returns 吸附后的本地平面点
   */
  public mouseCapture(
    params: MouseEventParams,
    options?: { startPoint?: IPoint; entities?: IEntity[] }
  ): IPoint {
    const currentWorldPos = { x: params.worldPos.x, y: params.worldPos.y, z: 0 }

    // 图元节点捕捉
    const captureRes = this.captureHelper.capture(params.screenPos, params.worldPos, undefined, {
      entities: options?.entities,
    })
    if (captureRes) {
      this.polarHelper.clearUI()
      return { x: captureRes.point.x, y: captureRes.point.y }
    }

    // 点云捕捉
    const pcRes = this.pointCloudHelper.getHitPosition(params.screenPos.x, params.screenPos.y)
    if (pcRes) {
      this.polarHelper.clearUI()
      return { x: currentWorldPos.x, y: currentWorldPos.y }
    }

    // 极轴
    const polarRes = this.polarHelper.calculatePolarSnap(currentWorldPos, {
      startPoint: options?.startPoint,
    })
    this.polarHelper.updateUI(params.screenPos, polarRes)
    if (polarRes) {
      return { x: polarRes.x, y: polarRes.y }
    }

    // 正交
    const orthoRes = this.orthogonalHelper.getOrthogonalInfo(currentWorldPos, {
      startPoint: options?.startPoint,
    })
    if (orthoRes) {
      return { x: orthoRes.x, y: orthoRes.y }
    }

    return { x: currentWorldPos.x, y: currentWorldPos.y }
  }

  /**
   * 统一清理辅助吸附相关的 UI 状态。
   *
   * 供工具切换、命令结束或异常中断时调用，保证提示层和参考线不会跨工具泄漏。
   */
  public clearUI() {
    this.captureHelper.clear()
    this.polarHelper.clearUI()
  }
}
